// Angular
import { Injectable } from '@angular/core';
// Service
import { ObjectUtils } from '@cv-invision/core';
// Model
import { PHOTO_SELECT_STATUS, SCHEMA_TYPE } from '../models/enums.model';
import { ViewsPosition, OrderDataChangedModel, SelectPhotoResponse } from '../models/views-service.model';
// Other
import { Subject } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class ViewsService {
	schema: any;
	document: any;
	etag: any;
	order: any;
	historical: any;
	photos = [];
	flattenedPhotos = {};
	// change photoSelected from string[] to Photo[]
	photosSelected = [];
	// Only for 3.4
	rooms: ViewsPosition = {
		exterior: [],
		interior: []
	};
	// Only for 3.4
	levels: [];
	form: any;
	selectedRule: any;
	privateToken: string;

	isEditing = false;
	historicalChanged = new Subject();
	selectedRuleChanged = new Subject();

	viewRuleSet = null;
	viewRuleSetChanged = new Subject();
	viewOrderDataChanged: Subject<OrderDataChangedModel> = new Subject();

	lendersAvailables: any = [];

	constructor() {
		//
	}

	/**
	 * set schema
	 *
	 * @param schema
	 */
	setSchema(schema: any): void {
		this.schema = schema;
	}

	/**
	 * set document
	 *
	 * @param document
	 */
	setDocument(document: any): void {
		this.document = document;
	}

	/**
	 * Set Last Etag for the Document
	 *
	 * @param document
	 */
	setEtag(etag: string): void {
		this.etag = etag;
	}

	/**
	 * set order
	 *
	 * @param order
	 */
	setOrder(order: any): void {
		this.order = order;
	}

	/**
	 * Set the Rule Sets if exist for the order
	 * @param ruleSet
	 */
	setViewRuleSet(ruleSet: any): void {
		const newRuleSet = Object.assign({}, ruleSet);
		newRuleSet.violations = Object.assign([], newRuleSet.violations);
		this.viewRuleSet = newRuleSet;
		this.viewRuleSetChanged.next(newRuleSet);
	}

	/**
	 * set historical
	 *
	 * @param historical
	 */
	setHistorical(historical: any): void {
		this.historical = historical;
		this.historicalChanged.next(historical);
	}

	/**
	 * Set the Rooms for this Order
	 * @param rooms
	 */
	setRooms(rooms: ViewsPosition): void {
		this.rooms = rooms;
	}

	/**
	 * Set all Levels for this Order
	 * @param rooms
	 */
	setLevels(levels: []): void {
		this.levels = levels;
	}

	/**
	 * set order photos
	 *
	 * @param photos
	 */
	setPhotos(photos: any): void {
		// photos.forEach(photo => {
		// 	const p = Object.assign({}, photo);
		// 	this.photos.push(p);
		// });
		this.photosSelected = [];
		this.flattenedPhotos = {};
		this.photos = ObjectUtils.recursiveDeepCopy(photos);

		this.photos.forEach(photo => {
			if (!(photo.refJsonPath in this.flattenedPhotos)) {
				const nObj = [];
				nObj[photo.refJsonPath] = [];

				Object.assign(this.flattenedPhotos, nObj);
			}

			const exist = this.flattenedPhotos[photo.refJsonPath].filter(item => item.id === photo.id) || [];

			if (exist.length === 0) {
				this.flattenedPhotos[photo.refJsonPath].push(photo);
			}
		});
	}

	/**
	 * Deletes the photos within the jsonPath and pushe the new ones.
	 *
	 * @param jsonPath
	 * @param photos
	 */
	patchPhotos(jsonPath: string, photos: any[]): void {
		const photosFiltered = this.photos.filter(photo => 'refJsonPath' in photo && photo.refJsonPath && photo.refJsonPath.indexOf(jsonPath) === -1);
		this.photos = photosFiltered.concat(photos);

		Object.keys(this.flattenedPhotos).forEach((photoJsonPath: any) => {
			if (photoJsonPath.indexOf(jsonPath) > -1) {
				delete(this.flattenedPhotos[photoJsonPath]);
				// this.flattenedPhotos[photoJsonPath] = [];
			}
		});

		photos.forEach(photo => {
			if (!(photo.refJsonPath in this.flattenedPhotos)) {
				const nObj = [];
				nObj[photo.refJsonPath] = [];

				Object.assign(this.flattenedPhotos, nObj);
			}

			const exist = this.flattenedPhotos[photo.refJsonPath] || [].filter(item => item.id === photo.id) || [];

			if (exist.length === 0) {
				this.flattenedPhotos[photo.refJsonPath].push(photo);
			}
		});
	}

	/**
	 * Clears the photos selected array.
	 */
	clearPhotosSelected(): void {
		this.photosSelected = [];
	}

	/**
	 * Add all photos to the selected array.
	 */
	selectAllPhotos(schema?: string): void {
		this.photosSelected = this.photos.filter((photo: any) => {
			let response = true;
			let imageType = '';
			switch (schema) {
				case SCHEMA_TYPE.CVDS_33: {
					imageType = ((photo.metadata && photo.metadata.imageType) || '').toUpperCase();
					response = ['HDR', 'INSPECT'].indexOf(imageType) > -1;
					// Special Types: Sketch and SmartTags
					if ((photo.metadata && photo.metadata.type && photo.metadata.type === 'SKETCH') ||
						(photo.refJsonPath && photo.refJsonPath.indexOf('smartTags') > -1)) {
						response = true;
					}
					break;
				}
				case SCHEMA_TYPE.CVDS_34:
				case SCHEMA_TYPE.CVDS_40:
					imageType = ((photo.metadata && (photo.metadata.imageType || photo.metadata.type)) || '').toUpperCase();
					response = ['HDR', 'STREET_ADDRESS'].indexOf(imageType) > -1;
					// Special Types: Sketch and SmartTags
					if ((photo.metadata && photo.metadata.type && photo.metadata.type === 'SKETCH') ||
						(photo.refJsonPath && photo.refJsonPath.indexOf('smartTags') > -1)) {
						response = true;
					}
					break;

				case SCHEMA_TYPE.CVDS_32:
				default:
					response = true;
					break;
			}

			return response;
		}).map((photo: any) => photo.id);
	}

	/**
	 * Select photo and return SelectPhotoResponse with the status and photoID
	 *
	 * @param id
	 * @returns @see SelectPhotoResponse
	 */
	selectPhoto(id: any): SelectPhotoResponse {
		const response = {
			status: PHOTO_SELECT_STATUS.SELECTED,
			id
		};
		const photoindex = this.photosSelected.indexOf(id);

		if (photoindex > -1) {
			this.photosSelected.splice(photoindex, 1);
			response.status = PHOTO_SELECT_STATUS.UNSELECTED;
		} else {
			this.photosSelected.push(id);
		}

		return response;
	}

	/**
	 * set form
	 *
	 * @param form
	 */
	setForm(form: any): void {
		this.form = form;
	}

	/**
	 * Set editing variable to switch between text and input views
	 *
	 * @param isEditing
	 */
	setEditing(isEditing: boolean): void {
		this.isEditing = isEditing;
	}

	/**
	 * set historical
	 *
	 * @param historical
	 */
	setSelectedRule(rule: any): void {
		this.selectedRule = rule;
		this.selectedRuleChanged.next(rule);
	}

	/**
	 * set privateToken
	 *
	 * @param token
	 */
	setPrivateToken(token: string): void {
		this.privateToken = token;
	}

	/**
	 * set lenders availables for the order
	 */
	setLenderAvailables(lenders : any){
		this.lendersAvailables = lenders
	}
}
